import { useState, useRef, useEffect, type ReactNode } from 'react';

/**
 * Minimal info-icon + tooltip primitive.
 * Desktop: hover toggles visibility.
 * Mobile / touch: click toggles. Click outside closes.
 *
 * Styling is Tailwind-only (no inline styles) so the strict
 * style-src 'self' CSP on wallet.gonkahub.com doesn't block it.
 */
export function InfoTooltip({
  children,
  ariaLabel,
}: {
  children: ReactNode;
  ariaLabel?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid="info-tooltip-trigger"
        aria-label={ariaLabel ?? 'Info'}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-text-tertiary text-[9px] font-semibold text-text-tertiary hover:text-text-primary hover:border-text-primary"
      >
        i
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-1/2 top-full z-50 mt-1.5 w-max max-w-[280px] -translate-x-1/2 rounded-lg border border-border-hairline bg-surface-2 px-3 py-2 text-[11px] leading-snug text-text-secondary shadow-lg"
        >
          {children}
        </div>
      )}
    </div>
  );
}
